import { useState, useCallback } from "react";
import type { GalaxyState } from "../../types/galaxy";
import { MapCanvas } from "./MapCanvas";
import { SystemPanel } from "./SystemPanel";
import { NewsFeed } from "./NewsFeed";
import { NavigationControls } from "./NavigationControls";
import { TTSToggle } from "../TTSToggle";

interface Props {
  galaxyState: GalaxyState;
  onWarp: (systemId: string, missionId?: string) => void;
  onResumeMission?: () => void;
  onMissionLogs?: () => void;
  onLogout?: () => void;
  displayName?: string;
}

export function GalaxyMap({ galaxyState, onWarp, onResumeMission, onMissionLogs, onLogout, displayName }: Props) {
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const [courseTarget, setCourseTarget] = useState<string | null>(null);

  const handleSelectSystem = useCallback((systemId: string | null) => {
    setSelectedSystem(systemId);
  }, []);

  const handleSetCourse = useCallback((systemId: string) => {
    setSelectedSystem(systemId);
    setCourseTarget(systemId);
  }, []);

  const handleConfirmWarp = useCallback(() => {
    if (!courseTarget) return;
    onWarp(courseTarget);
  }, [courseTarget, onWarp]);

  const handleCancelCourse = useCallback(() => {
    setCourseTarget(null);
  }, []);

  const handleEnterOrbit = useCallback(() => {
    if (!courseTarget) return;
    // If already at the target system, start encounter there
    onWarp(courseTarget);
  }, [courseTarget, onWarp]);

  return (
    <div className="galaxy-map">
      {/* Header bar */}
      <div className="gm-header">
        <div className="gm-header-left">
          <span className="gm-title">GALACTIC COMMAND</span>
          <span className="gm-subtitle">{galaxyState.player.shipName}</span>
        </div>
        <div className="gm-header-right">
          <span className="gm-faction">{galaxyState.player.faction}</span>
          {displayName && <span className="gm-user">{displayName}</span>}
          <TTSToggle />
          {onMissionLogs && (
            <button className="gm-logout" onClick={onMissionLogs}>MISSION LOGS</button>
          )}
          {onLogout && (
            <button className="gm-logout" onClick={onLogout}>LOG OUT</button>
          )}
        </div>
      </div>

      {/* Resume mission banner */}
      {onResumeMission && (
        <div className="gm-resume-banner">
          <span className="gm-resume-text">MISSION IN PROGRESS</span>
          <button className="gm-resume-btn" onClick={onResumeMission}>
            RESUME MISSION
          </button>
        </div>
      )}

      {/* Main content area */}
      <div className="gm-body">
        {/* Left: Map + News */}
        <div className="gm-main">
          {/* Map canvas */}
          <div className="gm-map-area">
            <MapCanvas
              galaxyState={galaxyState}
              selectedSystem={selectedSystem}
              onSelectSystem={handleSelectSystem}
              onSetCourse={handleSetCourse}
            />
          </div>

          {/* News feed */}
          <div className="gm-feed-area">
            <NewsFeed
              galaxyState={galaxyState}
              onSystemClick={handleSelectSystem}
              onSetCourse={handleSetCourse}
            />
          </div>
        </div>

        {/* Right: System panel */}
        <div className="gm-side">
          <SystemPanel
            galaxyState={galaxyState}
            selectedSystemId={selectedSystem}
            onSetCourse={handleSetCourse}
          />
        </div>
      </div>

      {/* Navigation bar */}
      <NavigationControls
        galaxyState={galaxyState}
        courseTarget={courseTarget}
        onConfirmWarp={handleConfirmWarp}
        onCancelCourse={handleCancelCourse}
        onEnterOrbit={handleEnterOrbit}
      />
    </div>
  );
}
